/**
 * What the Reports shell shares with its routed pages (index list, report
 * instance) and their canvases: paths, the <Outlet context> shape, and the
 * hooks to read it. Kept apart from the ReportsShell component so pages and
 * canvases import a small module rather than the shell itself.
 */
import { useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { ReportViewConfig, SalonReport } from "../../../types/reports";
import { ReportCatalog } from "./reportCatalog";

export const REPORTS_BASE_PATH = "/admin/reports";
export const reportPath = (id: string): string => `${REPORTS_BASE_PATH}/${id}`;

/**
 * What an open report's canvas (v2) offers the shell so "Duplicate" copies the
 * view on screen: the unsaved draft moves to the copy and the original
 * reverts to its saved view.
 */
export interface ReportDraftSource {
  /** The unsaved view to copy, or null while the view is clean. */
  getDraftViewConfig: () => ReportViewConfig | null;
  /** Revert the open report to its saved view (its draft moved to a copy). */
  discardDraft: () => void;
}

/** What the shell hands its routed children through `<Outlet context>`. */
export interface ReportsShellContext {
  salonId: string;
  userId: string | null;
  catalog: ReportCatalog;
  /** NEW reports in the Add-a-report picker (the "N new" pill). */
  newReportCount: number;
  reports: SalonReport[];
  /** Phone layout: a full-screen list at the index, one report per screen. */
  isMobile: boolean;
  loading: boolean;
  loadError: string | null;
  /** Report being removed (legacy ReportView shows its delete spinner). */
  removingReportId: string | null;
  /** Report whose name editor should open on arrival (Add again / Duplicate). */
  editingReportId: string | null;
  clearEditingReport: () => void;
  openAddReport: () => void;
  updateReportInList: (report: SalonReport) => void;
  requestRemove: (report: SalonReport) => void;
  duplicateReport: (report: SalonReport) => void;
  /** Open the Share modal for a report; null when the caller can't share
   *  (reports.canShareReports off), which hides the menu item. */
  requestShare: ((report: SalonReport) => void) | null;
  saveReportMeta: (
    report: SalonReport,
    name: string,
    description: string,
  ) => Promise<boolean>;
  /** Report canvases flag unsaved views here (sidebar amber dot). */
  setReportDirty: (reportId: string, dirty: boolean) => void;
  /** Report canvases register their draft for Duplicate (see
   *  `useReportDraftSource`). Returns the unregister function. */
  registerDraftSource: (
    reportId: string,
    source: ReportDraftSource,
  ) => () => void;
}

export const useReportsShell = (): ReportsShellContext =>
  useOutletContext<ReportsShellContext>();

/**
 * For a report canvas: register its unsaved draft so the header's Duplicate
 * copies what's on screen. `source` may change every render; the latest one
 * is always used.
 */
export function useReportDraftSource(
  reportId: string,
  source: ReportDraftSource,
): void {
  const { registerDraftSource } = useReportsShell();
  const latest = useRef(source);
  latest.current = source;
  useEffect(
    () =>
      registerDraftSource(reportId, {
        getDraftViewConfig: () => latest.current.getDraftViewConfig(),
        discardDraft: () => latest.current.discardDraft(),
      }),
    [registerDraftSource, reportId],
  );
}
